/**
 * SynkrManager - Universal Synchronization System
 * 
 * CHANGE HISTORY:
 * - 251102_1647 danielbeer: Integrate Profylr for sync context and rules
 * - 251026_0655 [pending] danielbeer: Refactored with Actr integration and TypeScript
 * 
 * Features:
 * - Multi-way sync (one-way, two-way, multi-way)
 * - Delta sync (minimal data transfer)
 * - Conflict resolution with configurable rules
 * - Freshness tracking
 * - Real-time and periodic sync
 * - Offline support with queue
 * - Actr integration for data operations
 * - Profylr integration for sync context
 */

import { nanoid } from 'nanoid';
import type { ProfileManager, ResolvedProfile } from '@opcentral/profylr';
import type {
  SynkrConfig,
  SyncAdapterConfig,
  SyncChange,
  SyncConflict,
  SyncResult,
  SyncStatus,
  FreshnessConfig,
  SyncHistoryEntry,
  SyncListener,
  SyncMode,
  SyncStrategy,
  ConflictResolution,
} from './types.js';
import type { BaseSyncAdapter } from './adapters/BaseSyncAdapter.js';

export class SynkrManager {
  private config: Required<SynkrConfig>;
  private adapters: Map<string, BaseSyncAdapter>;
  private syncQueue: SyncChange[];
  private conflicts: SyncConflict[];
  private syncing: boolean;
  private lastSync: Map<string, number>;
  private syncHistory: SyncHistoryEntry[];
  private freshnessConfig: Map<string, FreshnessConfig>;
  private listeners: Set<SyncListener>;
  private syncTimer: NodeJS.Timeout | null;
  private freshnessTimer: NodeJS.Timeout | null;
  private isOnline: boolean;
  private actr: any; // ActrManager instance
  private interactr: any; // InteractrManager instance
  private authr: any; // AuthrManager instance
  private profylr: ProfileManager | null; // ProfileManager instance

  constructor(options: SynkrConfig = {}) {
    this.config = {
      mode: options.mode || 'two-way',
      strategy: options.strategy || 'periodic',
      interval: options.interval || 30000,
      conflictResolution: options.conflictResolution || 'last-write-wins',
      enableOffline: options.enableOffline !== false,
      maxRetries: options.maxRetries || 3,
      retryDelay: options.retryDelay || 5000,
      actr: options.actr || null,
      interactr: options.interactr || null,
      authr: options.authr || null,
      profylr: options.profylr || null,
    };

    this.actr = this.config.actr;
    this.interactr = this.config.interactr;
    this.authr = this.config.authr;
    this.profylr = this.config.profylr;
    this.adapters = new Map();
    this.syncQueue = [];
    this.conflicts = [];
    this.syncing = false;
    this.lastSync = new Map();
    this.syncHistory = [];
    this.freshnessConfig = new Map();
    this.listeners = new Set();
    this.syncTimer = null;
    this.freshnessTimer = null;
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    // Setup online/offline listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.handleOffline());
    }
  }

  /**
   * Register sync adapter
   */
  registerAdapter(name: string, adapter: BaseSyncAdapter, config: SyncAdapterConfig = {}): void {
    this.adapters.set(name, adapter);

    // Setup freshness config
    this.freshnessConfig.set(name, {
      auto: config.autoFreshness !== false,
      interval: config.freshnessInterval || 300000, // 5 minutes
      threshold: config.freshnessThreshold || 0.7,
      lastCheck: Date.now(),
    });

    // Initialize last sync
    this.lastSync.set(name, 0);
  }

  /**
   * Unregister sync adapter
   */
  unregisterAdapter(name: string): void {
    const adapter = this.adapters.get(name);
    if (adapter && adapter.stopRealtime) {
      adapter.stopRealtime();
    }
    this.adapters.delete(name);
    this.freshnessConfig.delete(name);
    this.lastSync.delete(name);
  }

  /**
   * Start automatic sync
   */
  start(): void {
    if (this.config.strategy === 'periodic') {
      this.startPeriodicSync();
    } else if (this.config.strategy === 'real-time') {
      this.startRealtimeSync();
    }

    // Start freshness monitoring
    this.startFreshnessMonitoring();
  }

  /**
   * Stop automatic sync
   */
  stop(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }

    if (this.freshnessTimer) {
      clearInterval(this.freshnessTimer);
      this.freshnessTimer = null;
    }

    // Stop all real-time adapters
    for (const adapter of this.adapters.values()) {
      if (adapter.stopRealtime) {
        adapter.stopRealtime();
      }
    }
  }

  /**
   * Start periodic sync
   */
  private startPeriodicSync(): void {
    this.syncTimer = setInterval(() => {
      this.syncAll();
    }, this.config.interval);
  }

  /**
   * Start real-time sync
   */
  private startRealtimeSync(): void {
    // Setup WebSocket connections for real-time sync
    for (const [name, adapter] of this.adapters) {
      if (adapter.supportsRealtime && adapter.supportsRealtime()) {
        adapter.startRealtime((change: SyncChange) => {
          this.handleRealtimeChange(name, change);
        });
      }
    }
  }

  /**
   * Get active profile from Profylr
   */
  async getActiveProfile(): Promise<ResolvedProfile | null> {
    if (!this.profylr) {
      return null;
    }
    
    try {
      return await this.profylr.getActiveProfile();
    } catch (error) {
      console.warn('[Synkr] Failed to get active profile:', error);
      return null;
    }
  }

  /**
   * Check if sync is allowed by profile
   * Uses profile context to determine sync rules (e.g., compliance, data residency)
   */
  private async isSyncAllowedByProfile(adapterName: string): Promise<boolean> {
    const profile = await this.getActiveProfile();
    
    if (!profile?.context) {
      return true; // No profile restrictions
    }
    
    const context = profile.context;
    
    // Check compliance level restrictions
    if (context.complianceLevel === 'strict' && adapterName.includes('external')) {
      console.log(`[Synkr] Sync blocked by strict compliance for ${adapterName}`);
      return false;
    }
    
    // Check data residency requirements
    if (context.dataResidency && adapterName.includes('cloud')) {
      console.log(`[Synkr] Checking data residency requirements for ${adapterName}`);
      // Could check if adapter respects data residency
    }
    
    return true;
  }

  /**
   * Apply profile sync rules to sync operation
   * Enhances sync with profile-based context (compliance, priorities, etc.)
   */
  private async applyProfileSyncRules(adapterName: string): Promise<{ allowed: boolean; rules?: any }> {
    const profile = await this.getActiveProfile();
    
    if (!profile?.context) {
      return { allowed: true };
    }
    
    const context = profile.context;
    const rules: any = {};
    
    // Apply compliance rules
    if (context.complianceLevel) {
      rules.complianceLevel = context.complianceLevel;
      rules.encryptionRequired = context.complianceLevel === 'strict';
    }
    
    // Apply data residency rules
    if (context.dataResidency) {
      rules.dataResidency = context.dataResidency;
    }
    
    // Apply priority rules
    if (context.priority) {
      rules.priority = context.priority;
    }
    
    return {
      allowed: await this.isSyncAllowedByProfile(adapterName),
      rules,
    };
  }

  /**
   * Start freshness monitoring
   */
  private startFreshnessMonitoring(): void {
    this.freshnessTimer = setInterval(() => {
      this.checkFreshness();
    }, 60000); // Check every minute
  }

  /**
   * Sync all adapters with profile-based rules
   * 
   * PROFYLR INTEGRATION:
   * 1. Get active profile from Profylr
   * 2. Check if sync is allowed by profile context
   * 3. Apply profile sync rules (compliance, data residency)
   * 4. Execute sync with profile-aware settings
   */
  async syncAll(): Promise<{ success: any[]; failed: any[]; conflicts: any[] }> {
    if (this.syncing) {
      console.log('Sync already in progress');
      return { success: [], failed: [], conflicts: [] };
    }

    if (!this.isOnline && !this.config.enableOffline) {
      console.log('Offline - skipping sync');
      return { success: [], failed: [], conflicts: [] };
    }

    this.syncing = true;
    this.notifyListeners('syncStart', { adapters: Array.from(this.adapters.keys()) });

    const results = {
      success: [] as any[],
      failed: [] as any[],
      conflicts: [] as any[],
    };

    try {
      for (const [name, adapter] of this.adapters) {
        try {
          // Check profile sync rules
          const profileRules = await this.applyProfileSyncRules(name);
          
          if (!profileRules.allowed) {
            console.log(`[Synkr] Sync skipped for ${name} - blocked by profile`);
            results.failed.push({ name, error: 'Blocked by profile rules' });
            continue;
          }
          
          const result = await this.syncAdapter(name, adapter, profileRules.rules);
          results.success.push({ name, ...result });
        } catch (error) {
          console.error(`Sync failed for ${name}:`, error);
          results.failed.push({ name, error: error.message });
        }
      }

      // Process sync queue
      await this.processSyncQueue();

      this.notifyListeners('syncComplete', results);
    } catch (error) {
      this.notifyListeners('syncError', { error: error.message });
    } finally {
      this.syncing = false;
    }

    return results;
  }

  /**
   * Sync single adapter with optional profile rules
   */
  private async syncAdapter(name: string, adapter: BaseSyncAdapter, profileRules?: any): Promise<SyncResult> {
    const lastSyncTime = this.lastSync.get(name) || 0;
    const mode = this.config.mode;

    const result: SyncResult = {
      pulled: 0,
      pushed: 0,
      conflicts: 0,
    };
    
    // Log profile rules if present
    if (profileRules && Object.keys(profileRules).length > 0) {
      console.log(`[Synkr] Applying profile rules for ${name}:`, profileRules);
    }

    try {
      // Pull changes (if two-way or multi-way)
      if (mode === 'two-way' || mode === 'multi-way') {
        const changes = await adapter.pull(lastSyncTime);

        for (const change of changes) {
          const conflict = await this.applyChange(name, change);
          if (conflict) {
            this.conflicts.push(conflict);
            result.conflicts++;
          } else {
            result.pulled++;
          }
        }
      }

      // Push changes (if not one-way pull)
      if (mode !== 'one-way' || adapter.getDirection() === 'push') {
        const localChanges = await this.getLocalChanges(name, lastSyncTime);

        for (const change of localChanges) {
          try {
            await adapter.push(change);
            result.pushed++;
          } catch (error) {
            console.error(`Failed to push change:`, error);
          }
        }
      }

      // Update last sync time
      this.lastSync.set(name, Date.now());

      // Add to history
      this.syncHistory.push({
        adapter: name,
        timestamp: Date.now(),
        result,
      });

      return result;
    } catch (error) {
      throw new Error(`Sync failed for ${name}: ${error.message}`);
    }
  }

  /**
   * Apply change from remote
   */
  private async applyChange(adapterName: string, change: SyncChange): Promise<SyncConflict | null> {
    if (!this.actr) {
      console.warn('Actr not configured - cannot apply changes');
      return null;
    }

    try {
      // Get local version using Actr
      const local = await this.actr.read(change.entityType, change.id);

      if (!local) {
        // No local version - create new
        if (change.type === 'create' || change.type === 'update') {
          await this.actr.create(change.entityType, change.data);
        }
        return null;
      }

      // Check for conflict
      if (this.hasConflict(local, change.data)) {
        return this.handleConflict(adapterName, change.entityType, change.id, local, change.data);
      }

      // No conflict - apply change
      if (change.type === 'update') {
        await this.actr.update(change.entityType, change.id, change.data);
      } else if (change.type === 'delete') {
        await this.actr.delete(change.entityType, change.id);
      }

      return null;
    } catch (error) {
      console.error('Error applying change:', error);
      return null;
    }
  }

  /**
   * Check if there's a conflict
   */
  private hasConflict(local, remote): boolean {
    // If local has been modified since last sync
    if (local._modified && remote._modified) {
      return local._modified > remote._modified;
    }

    // Check if data differs
    const localData = { ...local };
    const remoteData = { ...remote };
    delete localData._modified;
    delete localData._version;
    delete remoteData._modified;
    delete remoteData._version;

    return JSON.stringify(localData) !== JSON.stringify(remoteData);
  }

  /**
   * Handle conflict
   */
  private async handleConflict(
    adapterName: string,
    entityType: string,
    entityId: string,
    local,
    remote: any
  ): Promise<SyncConflict | null> {
    const strategy = this.getConflictStrategy(adapterName);

    switch (strategy) {
      case 'last-write-wins':
        // Use most recent
        if (remote._modified > local._modified) {
          await this.actr.update(entityType, entityId, remote);
          return null;
        }
        return null;

      case 'local-wins':
        // Keep local version
        return null;

      case 'remote-wins':
        // Use remote version
        await this.actr.update(entityType, entityId, remote);
        return null;

      case 'manual':
      case 'custom':
        // Queue for manual resolution
        return {
          id: nanoid(),
          adapter: adapterName,
          entityType,
          entityId,
          local,
          remote,
          timestamp: Date.now(),
          status: 'pending',
        };

      default:
        return {
          id: nanoid(),
          adapter: adapterName,
          entityType,
          entityId,
          local,
          remote,
          timestamp: Date.now(),
          status: 'pending',
        };
    }
  }

  /**
   * Get conflict resolution strategy
   */
  private getConflictStrategy(adapterName: string): ConflictResolution {
    const adapter = this.adapters.get(adapterName);
    return adapter?.getConflictResolution() || this.config.conflictResolution;
  }

  /**
   * Resolve conflict manually
   */
  async resolveConflict(conflictId: string, resolution: 'local' | 'remote' | 'merge', mergedData?: any): Promise<void> {
    const conflict = this.conflicts.find((c) => c.id === conflictId);

    if (!conflict) {
      throw new Error(`Conflict ${conflictId} not found`);
    }

    if (!this.actr) {
      throw new Error('Actr not configured');
    }

    try {
      let dataToSave: any;

      switch (resolution) {
        case 'local':
          dataToSave = conflict.local;
          break;
        case 'remote':
          dataToSave = conflict.remote;
          break;
        case 'merge':
          if (!mergedData) {
            throw new Error('Merged data required for merge resolution');
          }
          dataToSave = mergedData;
          break;
      }

      // Save resolved data using Actr
      await this.actr.update(conflict.entityType, conflict.entityId, dataToSave);

      // Mark conflict as resolved
      conflict.status = 'resolved';
      conflict.resolution = resolution;

      // Remove from conflicts array
      this.conflicts = this.conflicts.filter((c) => c.id !== conflictId);

      this.notifyListeners('conflictResolved', { conflictId, resolution });
    } catch (error) {
      throw new Error(`Failed to resolve conflict: ${error.message}`);
    }
  }

  /**
   * Get local changes since last sync
   */
  private async getLocalChanges(adapterName: string, since: number): Promise<SyncChange[]> {
    if (!this.actr) {
      return [];
    }

    try {
      // Query Actr for changes since last sync
      // This assumes Actr tracks modification timestamps
      const changes: SyncChange[] = [];

      // Get all entity types that this adapter syncs
      const adapter = this.adapters.get(adapterName);
      if (!adapter) {
        return [];
      }

      const entityTypes = adapter.getEntityTypes();

      for (const entityType of entityTypes) {
        const entities = await this.actr.query(entityType, {
          _modified: { $gt: since },
        });

        for (const entity of entities) {
          changes.push({
            id: entity.id,
            type: 'update',
            entityType,
            data: entity,
            _modified: entity._modified,
            _version: entity._version,
          });
        }
      }

      return changes;
    } catch (error) {
      console.error('Error getting local changes:', error);
      return [];
    }
  }

  /**
   * Process sync queue (for offline changes)
   */
  private async processSyncQueue(): Promise<void> {
    if (this.syncQueue.length === 0) {
      return;
    }

    const queue = [...this.syncQueue];
    this.syncQueue = [];

    for (const change of queue) {
      try {
        // Find appropriate adapter
        for (const [name, adapter] of this.adapters) {
          if (adapter.getEntityTypes().includes(change.entityType)) {
            await adapter.push(change);
            break;
          }
        }
      } catch (error) {
        console.error('Failed to process queued change:', error);
        // Re-queue if failed
        this.syncQueue.push(change);
      }
    }
  }

  /**
   * Handle real-time change
   */
  private async handleRealtimeChange(adapterName: string, change: SyncChange): Promise<void> {
    const conflict = await this.applyChange(adapterName, change);

    if (conflict) {
      this.conflicts.push(conflict);
      this.notifyListeners('conflict', conflict);
    }
  }

  /**
   * Check freshness of all adapters
   */
  private async checkFreshness(): Promise<void> {
    for (const [name, config] of this.freshnessConfig) {
      if (!config.auto) {
        continue;
      }

      const lastSync = this.lastSync.get(name) || 0;
      const age = Date.now() - lastSync;
      const freshness = 1 - age / config.interval;

      if (freshness < config.threshold) {
        this.notifyListeners('freshnessLow', {
          adapter: name,
          freshness,
          threshold: config.threshold,
        });

        // Auto-sync if freshness is low
        const adapter = this.adapters.get(name);
        if (adapter) {
          await this.syncAdapter(name, adapter);
        }
      }
    }
  }

  /**
   * Handle online event
   */
  private handleOnline(): void {
    this.isOnline = true;
    this.notifyListeners('online', {});

    // Process sync queue
    this.processSyncQueue();
  }

  /**
   * Handle offline event
   */
  private handleOffline(): void {
    this.isOnline = false;
    this.notifyListeners('offline', {});
  }

  /**
   * Subscribe to sync events
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Add listener for sync status changes (alias for subscribe)
   */
  addListener(listener: SyncListener): void {
    this.listeners.add(listener);
  }

  /**
   * Remove listener for sync status changes
   */
  removeListener(listener: SyncListener): void {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(event: string, data?: any): void {
    for (const listener of this.listeners) {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    }
  }

  /**
   * Get sync status
   */
  getStatus(): SyncStatus {
    return {
      syncing: this.syncing,
      online: this.isOnline,
      adapters: Array.from(this.adapters.keys()),
      lastSync: Object.fromEntries(this.lastSync),
      queueSize: this.syncQueue.length,
      conflictCount: this.conflicts.length,
    };
  }

  /**
   * Get conflicts
   */
  getConflicts(): SyncConflict[] {
    return [...this.conflicts];
  }

  /**
   * Get sync history
   */
  getHistory(limit?: number): SyncHistoryEntry[] {
    if (limit) {
      return this.syncHistory.slice(-limit);
    }
    return [...this.syncHistory];
  }

  /**
   * Clear sync history
   */
  clearHistory(): void {
    this.syncHistory = [];
  }

  /**
   * Queue change for sync (offline mode)
   */
  queueChange(change: SyncChange): void {
    this.syncQueue.push(change);
  }
}

// Singleton instance
let instance: SynkrManager | null = null;

export function getSynkr(config?: SynkrConfig): SynkrManager {
  if (!instance) {
    instance = new SynkrManager(config);
  }
  return instance;
}

export default getSynkr;
